import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Box, Typography, IconButton, CircularProgress } from '@mui/material';
import { ArrowLeft } from '@phosphor-icons/react';
import { getWhatsappClientsChat } from '../../utils/apis';
import LayoutWithSidebar from '../../components/common/layoutWithSidebar';
import { memo, useCallback, useMemo } from 'react';
import WhatsappChatTabs from '../../components/Whatsapp/WhatsappChatTabs';

// Constants
const SELECTED_AGENT_KEY = 'selectedWhatsappAgent';

// Utility function to ensure phone number has a "+" prefix
const formatPhoneNumber = (phoneNumber: string) => {
  if (!phoneNumber) return '';
  return phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`;
};

// Get the stored agent from localStorage
const getStoredAgent = () => {
  try {
    const stored = localStorage.getItem(SELECTED_AGENT_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const WhatsappChatsPge = () => {
  const { whatsappPhonenumberId, phoneNumber } = useParams();
  const navigate = useNavigate();
  const storedAgent = useMemo(() => getStoredAgent(), []);

  const handleBack = useCallback(() => {
    navigate('/whatsapp-logs');
  }, [navigate]);

  const { data: whatsappClientsChat, isLoading: isWhatsappClientsChatLoading } =
    useQuery({
      queryKey: ['whatsappClientsChat', whatsappPhonenumberId],
      queryFn: () => getWhatsappClientsChat(whatsappPhonenumberId || ''),
      enabled: !!whatsappPhonenumberId,
      staleTime: 1000 * 60 * 1,
      retry: 0,
      gcTime: 1000 * 60 * 10,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    });

  const chats = useMemo(
    () =>
      whatsappClientsChat?.data?.data?.find(
        (item: any) => item.phoneNumber === phoneNumber
      ),
    [whatsappClientsChat, phoneNumber]
  );

  if (isWhatsappClientsChatLoading) {
    return (
      <LayoutWithSidebar margin={2}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <CircularProgress />
        </Box>
      </LayoutWithSidebar>
    );
  }

  return (
    <LayoutWithSidebar margin={2}>
      <Box sx={{ height: '100%' }}>
        {/* Header Section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={handleBack}>
              <ArrowLeft
                size={20}
                color="#000000"
                style={{
                  fontWeight: 600,
                }}
              />
            </IconButton>
            <Typography
              sx={{
                color: '#2B2D3F',
                fontSize: '16px',
                fontWeight: 600,
                lineHeight: 'normal',
                letterSpacing: '-0.16px',
              }}
            >
              {storedAgent?.agentName || 'Agent'} / {whatsappPhonenumberId} /{' '}
              {formatPhoneNumber(phoneNumber || '')}
            </Typography>
          </Box>
        </Box>

        <WhatsappChatTabs
          messages={chats?.chat || []}
          phoneNumber={phoneNumber || ''}
          whatsappPhonenumberId={whatsappPhonenumberId || ''}
          customerName={chats?.customer_name || ''}
          isLoading={isWhatsappClientsChatLoading}
        />
      </Box>
    </LayoutWithSidebar>
  );
};

export default memo(WhatsappChatsPge);
